#!/usr/bin/env node

/**
 * @wayai/cli — sync hub configuration between local files and the platform
 *
 * Commands:
 *   wayai login              — authenticate via OAuth (browser)
 *   wayai login --token      — authenticate via API token (headless)
 *   wayai logout             — clear stored credentials
 *   wayai status             — show current auth and config status
 *   wayai whoami             — show the authenticated identity (--json supported)
 *   wayai init               — scope the repo to an organization (wayai-ws/wayai.yaml)
 *   wayai create-credential  — create an organization credential
 *   wayai update-credential  — update / rotate an organization credential
 *   wayai pull               — fetch hub config and write local files (mirrors linked production read-only)
 *   wayai push               — parse local files, diff, and sync to preview (creates hub if new)
 *   wayai create [folder] [--label <l>] — explicitly create a new hub from an idless folder, then push
 *   wayai diff [--production]  — dry-run diff of local files vs preview (or the linked production hub)
 *   wayai replicate [hub] [--label <l>] — clone a hub into a new sibling preview
 *   wayai relabel <label> [--hub <h>]   — set a preview hub's label (--clear to remove)
 *   wayai publish            — promote the preview to production (first publish clones it; then syncs)
 *   wayai sync               — alias of `wayai publish`
 *   wayai org create        — create a new organization (you become its owner)
 *   wayai org <pull|push|diff> — sync org-scoped resources as code (org/ at repo root)
 *   wayai use [--add] <hub|base|community> — scope this worktree to a hub, base or community (--add widens)
 *   wayai unbind [<hub|base|community>] — drop one entry, or clear the whole worktree scope
 *   wayai sync-skills    — sync skills to Anthropic/OpenAI provider connections
 *   wayai sync-mcp       — re-discover an MCP connection's tool/resource catalog
 *   wayai alerts         — show a hub's active connection/credential alerts (run first when a hub misbehaves)
 *   wayai analytics      — show analytics summary and metrics
 *   wayai delete-history — delete your conversations on a hub (full history also clears user state; --conversation-id <id> deletes just one)
 *   wayai list           — list organizations and hubs
 *   wayai communities    — an org's communities: list, join link, stats, access list (org owners and admins)
 *   wayai contacts       — link a contact to its customer record in a base, or unlink it (org admins, browser sign-in)
 *   wayai knowledge migrate — move an org's, then a hub's, knowledge resources into its knowledge base
 *   wayai report create  — report a platform bug (submits to the triage queue)
 *   wayai update         — update CLI to the latest version
 *   wayai bases … etc.   — the Data surface: one top-level namespace per ontology
 *                          entity. Dispatched from the `default:` branch below so a
 *                          WayAI command always wins a name collision.
 */

import { readFileSync } from 'node:fs';
import { fileURLToPath } from 'node:url';
import { dirname, join } from 'node:path';
import { initSentry, captureException, shouldReportToSentry, closeSentry } from './lib/sentry.js';
import { friendlyHint } from './lib/errors.js';
import { maskSecretsInMessage } from './lib/mask-secrets.js';
import { wantsHelp, shouldInterceptHelp, entryFlagRefusal, OWN_HELP_COMMANDS } from './lib/utils.js';
import { DATA_NAMESPACES } from './data/registry.js';
import { refreshVersionCaches } from './lib/version-refresh.js';
import {
  showUpdateNudges,
  REFRESH_COMMAND,
  ENV_REFRESH_CLI,
  ENV_REFRESH_SKILL,
  ENV_REFRESH_ADMIN_SKILL,
} from './lib/update-nudge.js';

const __dirname = dirname(fileURLToPath(import.meta.url));
const pkg = JSON.parse(readFileSync(join(__dirname, '..', 'package.json'), 'utf-8'));

const [,, command, ...args] = process.argv;

// Hidden command: when invoked as `wayai __refresh-version-cache`, this process IS the detached
// background refresher (spawned by update-nudge.ts). It does the network work and exits — it never
// runs a real command, inits Sentry, or shows a nudge of its own (see the dispatch at EOF). The
// REFRESH_COMMAND / ENV_REFRESH_* names are imported from update-nudge.ts so producer and consumer
// can't drift.
const isBackgroundRefresh = command === REFRESH_COMMAND;

// `OWN_HELP_COMMANDS` (the set of subcommands that own their `--help` rendering)
// lives in ./lib/utils.js — single source of truth shared with the tests.

// Initialize Sentry early (before any command runs). Skip for the background refresher — it's a
// throwaway process that does no user-facing work and should never report to Sentry.
if (!isBackgroundRefresh) initSentry(command, pkg.version);

async function main(): Promise<void> {
  // Intercept `--help`/`-h` for a side-effecting subcommand BEFORE dispatch, so
  // e.g. `wayai run-eval --help` prints help instead of executing the command
  // (which would create + run a billable eval session). Commands that render
  // their own command-specific help (OWN_HELP_COMMANDS) fall through to their
  // handler so their richer help isn't shadowed by the global help.
  // Union built HERE rather than inside `lib/utils.ts`, so the base library
  // never imports from a feature subtree. Every Data namespace renders its own
  // command tree, which is what `wayai bases --help` should show — without this
  // the global help would shadow it.
  if (shouldInterceptHelp(command, args, new Set([...OWN_HELP_COMMANDS, ...DATA_NAMESPACES]))) {
    await printHelp();
    return;
  }

  // Before anything below can write: an argument the command's parser does not read (a `--dry-run`,
  // a `--flag=<value>` spelling, a value flag given no value, a positional) would be skipped, and the
  // command would run without it. Printed and exited, as the router's refusals are: an expected user
  // condition, kept out of Sentry.
  const flagRefusal = entryFlagRefusal(command, args, new Set(DATA_NAMESPACES));
  if (flagRefusal) {
    console.error(flagRefusal);
    process.exit(1);
  }

  // Repair Claude Code's `.claude/skills/wayai` symlink on any real command (not
  // help/version — `--help` must stay side-effect-free, mirroring the update-check
  // guard below). The `skills` installer skips the link when `.claude/` is absent;
  // healing here (not just in `status`) means the first `wayai` command the agent
  // runs fixes it. Self-guards (no-op unless this repo needs it); notice to stderr
  // so `--json` stays pure.
  if (command && !wantsHelp(args)) {
    const { findGitRoot } = await import('./lib/workspace.js');
    const { healSkillLinkForCommand } = await import('./lib/skill-symlink.js');
    if (healSkillLinkForCommand(command, findGitRoot() ?? process.cwd())) {
      console.error(
        'Linked .claude/skills/wayai → .agents/skills/wayai so Claude Code can load the WayAI skill.'
      );
    }
  }

  switch (command) {
    case 'login': {
      const { loginCommand } = await import('./commands/login.js');
      await loginCommand(args);
      break;
    }
    case 'logout': {
      const { logoutCommand } = await import('./commands/logout.js');
      await logoutCommand();
      break;
    }
    case 'status': {
      const { statusCommand } = await import('./commands/status.js');
      await statusCommand(args, pkg);
      break;
    }
    case 'whoami': {
      const { whoamiCommand } = await import('./commands/whoami.js');
      await whoamiCommand(args);
      break;
    }
    case 'init': {
      const { initCommand } = await import('./commands/init.js');
      await initCommand(args);
      break;
    }
    case 'pull': {
      const { pullCommand } = await import('./commands/pull.js');
      await pullCommand(args);
      break;
    }
    case 'push': {
      const { pushCommand } = await import('./commands/push.js');
      await pushCommand(args);
      break;
    }
    case 'create': {
      const { createCommand } = await import('./commands/create.js');
      await createCommand(args);
      break;
    }
    case 'diff': {
      const { diffCommand } = await import('./commands/diff.js');
      await diffCommand(args);
      break;
    }
    case 'replicate': {
      const { replicateCommand } = await import('./commands/replicate.js');
      await replicateCommand(args);
      break;
    }
    case 'relabel': {
      const { relabelCommand } = await import('./commands/relabel.js');
      await relabelCommand(args);
      break;
    }
    // `sync` is a bare alias of `publish` (distinct from `sync-skills` / `sync-mcp`,
    // which are exact-string cases). Both promote preview → production.
    case 'publish':
    case 'sync': {
      const { publishCommand } = await import('./commands/publish.js');
      await publishCommand(args);
      break;
    }
    case 'use': {
      const { useCommand } = await import('./commands/use.js');
      await useCommand(args);
      break;
    }
    case 'migrate': {
      const { migrateCommand } = await import('./commands/migrate.js');
      await migrateCommand(args);
      break;
    }
    // `unlock` is the deprecated pre-rename alias for `unbind` (kept for back-compat).
    case 'unbind':
    case 'unlock': {
      const { unbindCommand } = await import('./commands/unbind.js');
      await unbindCommand(args);
      break;
    }
    case 'send-message': {
      const { sendMessageCommand } = await import('./commands/send-message.js');
      await sendMessageCommand(args);
      break;
    }
    case 'conversations': {
      const { conversationsCommand } = await import('./commands/conversations.js');
      await conversationsCommand(args);
      break;
    }
    case 'alerts': {
      const { alertsCommand } = await import('./commands/alerts.js');
      await alertsCommand(args);
      break;
    }
    case 'delete-history': {
      const { deleteHistoryCommand } = await import('./commands/delete-history.js');
      await deleteHistoryCommand(args);
      break;
    }
    case 'sync-skills': {
      const { syncSkillsCommand } = await import('./commands/sync-skills.js');
      await syncSkillsCommand(args);
      break;
    }
    case 'sync-mcp': {
      const { syncMcpCommand } = await import('./commands/sync-mcp.js');
      await syncMcpCommand(args);
      break;
    }
    case 'evals': {
      const { evalsCommand } = await import('./commands/evals.js');
      await evalsCommand(args);
      break;
    }
    case 'run-eval': {
      const { runEvalCommand } = await import('./commands/run-eval.js');
      await runEvalCommand(args);
      break;
    }
    case 'eval-results': {
      const { evalResultsCommand } = await import('./commands/eval-results.js');
      await evalResultsCommand(args);
      break;
    }
    case 'eval': {
      const { evalCommand } = await import('./commands/eval.js');
      await evalCommand(args);
      break;
    }
    case 'analytics': {
      const { analyticsCommand } = await import('./commands/analytics.js');
      await analyticsCommand(args);
      break;
    }
    case 'create-credential': {
      const { createCredentialCommand } = await import('./commands/create-credential.js');
      await createCredentialCommand(args);
      break;
    }
    case 'update-credential': {
      const { updateCredentialCommand } = await import('./commands/update-credential.js');
      await updateCredentialCommand(args);
      break;
    }
    case 'set-connection-credential': {
      const { setConnectionCredentialCommand } = await import('./commands/set-connection-credential.js');
      await setConnectionCredentialCommand(args);
      break;
    }
    case 'org': {
      const { orgCommand } = await import('./commands/org.js');
      await orgCommand(args);
      break;
    }
    case 'list': {
      const { listCommand } = await import('./commands/list.js');
      await listCommand(args);
      break;
    }
    case 'communities': {
      const { communitiesCommand } = await import('./commands/communities.js');
      await communitiesCommand(args);
      break;
    }
    case 'contacts': {
      const { contactsCommand } = await import('./commands/contacts.js');
      await contactsCommand(args);
      break;
    }
    case 'knowledge': {
      const { knowledgeCommand } = await import('./commands/knowledge.js');
      await knowledgeCommand(args);
      break;
    }
    case 'admin': {
      const { adminCommand } = await import('./commands/admin.js');
      await adminCommand(args);
      break;
    }
    case 'report': {
      const { reportCommand } = await import('./commands/report.js');
      await reportCommand(args, pkg.version);
      break;
    }
    case 'update': {
      const { updateCommand } = await import('./commands/update.js');
      await updateCommand(pkg);
      break;
    }
    case 'help':
    case '--help':
    case '-h':
    case undefined:
      await printHelp();
      break;
    case '--version':
    case '-v':
      console.log(pkg.version);
      break;
    default: {
      // The Data (Bases) surface is consulted only here, AFTER every WayAI case
      // has had its chance to match. That ordering is what makes the merge's
      // collision rule structural: a Data namespace cannot shadow a WayAI
      // command even if someone registers a colliding word.
      const { isDataNamespace, runDataCommand } = await import('./data/program.js');
      if (isDataNamespace(command)) {
        await runDataCommand(command, args);
        break;
      }
      console.error(`Unknown command: ${command}`);
      await printHelp();
      process.exit(1);
    }
  }
}

/**
 * The Data namespaces, listed from the registry rather than a hand-kept literal.
 *
 * The previous static block named only `bases` and told the reader to run
 * `wayai bases --help` "for the full tree" — but the other namespaces are its
 * SIBLINGS, not its subcommands, so that command never mentions them and the
 * sentence pointed at a tree that does not contain them. Deriving the list means
 * a namespace cannot ship unreachable from help again.
 */
async function dataHelpBlock(): Promise<string> {
  const { buildDataProgram } = await import('./data/program.js');
  const namespaces = buildDataProgram().commands;
  const width = Math.max(...namespaces.map((c) => c.name().length));
  return namespaces
    .map((c) => `  ${c.name().padEnd(width)}  ${c.description()}`.trimEnd())
    .join('\n');
}

async function printHelp(): Promise<void> {
  console.log(`
wayai — WayAI CLI

Usage:
  wayai <command> [options]

Commands:
  login              Authenticate via OAuth (opens browser)
  login --token      Authenticate via API token (for headless/CI)
  logout             Clear stored credentials
  status             Show current auth and config status
  whoami             Show the authenticated identity (supports --json)
  init               Scope this repo to an organization (pick one)
  create-credential  Create an organization credential (API key, token, etc.)
  update-credential  Update / rotate an organization credential (by name)
  set-connection-credential  Set a connection's credential directly (org link or --field/--stdin), preview or production
  pull               Fetch config and write local files — \`hubs/<hub>\`, \`bases/<base>\` or \`communities/<community>\` (also mirrors the linked production hub/base read-only)
  push               Parse local files, show diff, sync to preview — \`hubs/<hub>\` or \`bases/<base>\` (creates the hub/base if new); \`communities/<community>\` edits the live community
  create [folder]    Explicitly create a new hub from an idless folder, then push (--label to name the preview)
  diff               Dry-run diff of local files vs the platform (--production diffs vs the linked production hub)
  replicate          Clone a hub into a new sibling preview (--label to name it)
  relabel            Set a preview hub's label (--clear to remove it)
  publish            Promote the preview to production (first publish clones it; subsequent runs sync changes)
  sync               Alias of \`publish\` (not to be confused with sync-skills / sync-mcp)
  org create         Create a new organization (you become its owner)
  org <pull|push|diff>  Sync org-scoped resources as code (wayai-ws/org/)
  use [--add] <sel>  Scope this worktree to a hub, base or community (UUID, folder name, hubs/<n>, bases/<n>, communities/<n>; --add widens)
  migrate            Move a legacy repo to the wayai-ws/{hubs,org} layout
  unbind [<sel>]     Drop one entry from the worktree scope, or clear the whole scope
  send-message       Send a test message to a hub (preview or production; -f <path> to attach a file)
  conversations      List or inspect conversations
  alerts             Show a hub's active connection/credential alerts (run this FIRST when a hub misbehaves)
  analytics          Show analytics summary and metrics (use \`analytics query\` for structured ClickHouse queries, \`analytics sql\` for read-only SQL over conversation + per-message cost)
  delete-history     Delete your conversations on a hub; full history also clears your user-scoped state (--conversation-id <id> deletes just one, keeps state)
  evals              List evaluation scenarios for the current hub (use \`evals sql\` for read-only SQL on eval results)
  run-eval           Run a scenario set's enabled evals and show results (--eval to run only named scenarios)
  eval-results       View results from a completed eval session
  eval capture       Capture a real conversation as a scenario YAML
  eval session stop  Cancel a running eval session (recovery for an interrupted run-eval)
  eval session delete  Delete one eval session (\`--all\` for every session on the hub)
  eval call          Place scored voice calls for an eval journey (a machine caller; spends provider minutes)
  sync-skills        Sync skills to Anthropic/OpenAI provider connections
  sync-mcp           Re-discover an MCP connection's tools (refresh stale schemas)
  list               List organizations and hubs
  communities        An organization's communities: list, join link (get/rotate/off), stats, access list (list/add/remove) — \`wayai communities --help\`
  contacts           Link a contact to its customer record in a base, or unlink it (browser sign-in only) — \`wayai contacts --help\`
  knowledge migrate  Move an organization's knowledge resources, then a hub's (--hub), into its knowledge base as file types and files — \`wayai knowledge --help\`
  report create      Report a platform bug (submits to the triage queue)
  report edit        Amend your own pending report (title/description/error/steps/context)
  update             Update CLI to the latest version

Data — one namespace per entity. Run \`wayai <namespace> --help\` for its tree.
${await dataHelpBlock()}
  bases promote      Promote a preview base to production (distinct from \`publish\`, which promotes a hub)
  bases use <base>   Deprecated alias of \`wayai use bases/<id>\`
  bases unbind       Deprecated alias of \`wayai unbind bases/<id>\` (clears the base axis)

Flags:
  --yes, -y              Skip confirmation prompts (useful for CI and scripting)
  --hub <uuid|name>      Target hub when the workspace has more than one (push, pull, diff, replicate, relabel, publish)
  --base <id|name>       Target base for pull/push — routes the invocation to the \`bases/\` subtree (never combined with --hub)
  --community <id|folder>  Target community for pull/push — routes to the \`communities/\` subtree (never combined with --hub or --base)
  --dry-run              List the changes only (push of a base or a community; pull of a community)
  --theirs               Take the server's side of each conflict (pull of a community)
  --production           Diff local preview files against the linked production hub (diff)
  --label <name>         Preview label — for the new sibling (replicate) or a brand-new hub (create, or push on create)
  --clear                Remove the preview label (relabel)
  --name <name>          Credential name (create-credential; target for update-credential)
  --type <auth-type>     Auth type: "api_key", "bearer", "basic_auth" (create-credential)
  --rename <name>        New name for the credential (update-credential)
  --tag <name>           Org tag to scope the credential — repeatable or comma-separated (create/update-credential)
  --environment <env>    Credential environment: "preview", "production", "all" (create/update-credential)
  --stdin                Read secret from stdin pipe (create/update-credential)
  --secret               Prompt for the new secret interactively (update-credential)
  --org <uuid>           Organization ID (create-credential, update-credential, list)
  --region <region>      DO placement hint for a new org (org create) — e.g. wnam, weur, sam, apac
  --connection-id <uuid> Limit sync-skills to a specific connection
  --connection <name>    MCP connection to re-sync — display name or UUID (sync-mcp)
  --enabled/--disabled   Filter evals by status (evals)
  --no-wait              Don't wait for eval session to complete (run-eval)
  --no-queue             Fail immediately instead of waiting for the fixture to free up (run-eval; implied by --no-wait)
  --timeout <seconds>    Max wait time for eval session (default: 600)
  --pacing <preset|ms>   Run pacing: conservative | balanced | fast | <milliseconds> (run-eval; default balanced)
  --fixture <name>       Override the suite's declared seed fixture for this run (run-eval)
  --call-mode            Run every turn as a voice call's is written: the call-mode addendum, no gate rewrite (run-eval)
  --set <name>           Run a specific scenario set (run-eval)
  --session <uuid>       Specify eval session ID (eval-results)
  --eval <name>          Run only the named scenario — repeatable (run-eval); filter by eval name (eval-results)
  --runs <list>          Run only these repetitions of one --eval scenario, e.g. 2,6 (run-eval); show individual run details (eval-results)
  --json                 Output raw JSON (status, whoami, list, org create, eval-results, analytics, conversations)
  --period <period>      Time period: 24h, 7d, 30d, 3m, 1y (analytics, conversations)
  --from <date>          Start date ISO (2026-01-01) (analytics, conversations)
  --to <date>            End date ISO (2026-01-31) (analytics, conversations)
  --metric <names>       Comma-separated metric names (analytics) — for \`analytics query\` use "col=...,agg=...[,alias=...,corr_with=...,level=...]"
  --filter <expr>        Filter expression (repeatable). \`analytics\`: "<name> <op> <value>". \`analytics query\`: "<column> <op> <value>"
  --group-by <cols>      Comma-separated column paths (analytics query)
  --order-by <expr>      "<column>:asc|desc" (analytics query, repeatable)
  --limit <n>            Max rows (analytics query, analytics sql, max 10000)
  --schema               List readable tables, columns, and variable paths (analytics sql: conversation + message; evals sql: conversation)
  --list-metrics         List available metrics for the hub (analytics)
  --version, -v          Show CLI version

Org-scoped mode:
  Each repository is scoped to a single organization via wayai-ws/wayai.yaml
  (only \`organization_id\` is required). Hubs self-identify via \`hub.yaml\`
  inside their folder under \`workspace/\`.

  Run \`wayai init\` to pick the organization (add -y to skip prompts).
  pull/push operate on the single hub in the workspace, or pass
  \`--hub <uuid|name>\` to disambiguate when multiple hubs exist.

Worktree scope:
  Each git checkout (main or linked worktree) carries a scope: a set of hubs, a
  set of bases and a set of communities, at \`<git-dir>/wayai-scope\` —
  per-checkout, never tracked.
  push/pull/publish refuse to run against anything outside a non-empty set. Each
  axis is seeded by the first successful pull/push/create into a checkout that
  has none, and only widens on purpose: \`wayai use --add <sel>\` adds one,
  \`wayai use <sel>\` replaces that axis, \`wayai unbind <sel>\` drops one entry and
  bare \`wayai unbind\` clears everything. It is a routing tripwire, not a lock
  and not authorization.

Examples:
  wayai login
  wayai init
  wayai pull
  wayai push
  wayai push -y
  `.trim());
}

const SKIP_UPDATE_CHECK = ['--version', '-v', '--help', '-h', 'help', 'update'];

async function runForeground(): Promise<void> {
  try {
    await main();
    if (command && !SKIP_UPDATE_CHECK.includes(command) && !wantsHelp(args)) {
      showUpdateNudges(pkg.version, fileURLToPath(import.meta.url));
    }
    await closeSentry();
  } catch (err) {
    if (shouldReportToSentry(err)) {
      captureException(err);
    }
    await closeSentry();
    console.error(maskSecretsInMessage(err instanceof Error ? err.message : String(err)));
    const hint = friendlyHint(err);
    if (hint) console.error(hint);
    process.exit(1);
  }
}

if (isBackgroundRefresh) {
  // We ARE the detached refresher: do the network work, write the cache, exit. No command, no nudge.
  void refreshVersionCaches({
    cli: process.env[ENV_REFRESH_CLI] === '1',
    skill: process.env[ENV_REFRESH_SKILL] === '1',
    adminSkill: process.env[ENV_REFRESH_ADMIN_SKILL] === '1',
  }).finally(() => process.exit(0));
} else {
  void runForeground();
}
